import { apiClient } from './api'

// Admin Merchant Types
export interface AdminMerchant {
  id: string
  company_name: string
  name?: string
  email: string
  description?: string
  website?: string
  status: 'pending' | 'enabled' | 'disabled' | 'suspended'
  is_verified: boolean
  currency?: string
  country?: string
  payout_crypto_enabled: boolean
  api_key?: string
  api_secret?: string
  verification_date?: string
  verification_notes?: string
  created_at: string
  updated_at: string
}

export interface AdminMerchantCreateData {
  // User fields
  email: string
  password: string
  first_name: string
  last_name: string
  phone_number?: string
  
  // Merchant fields
  company_name: string
  description?: string
  website?: string
  currency?: string
  country?: string
  payout_crypto_enabled?: boolean
}

export interface AdminMerchantUpdateData {
  company_name?: string
  description?: string
  website?: string
  status?: 'pending' | 'enabled' | 'disabled' | 'suspended'
  currency?: string
  country?: string
  payout_crypto_enabled?: boolean
  verification_notes?: string
}

export interface AdminMerchantStats {
  merchant_id: string
  company_name: string
  total_users: number
  total_submerchants: number
  total_transactions: number
  total_revenue: string
  status: string
  is_verified: boolean
  created_at: string
  last_activity?: string
  payout_configured: boolean
  api_credentials_set: boolean
}

export interface AdminMerchantBulkAction {
  merchant_ids: string[]
  verification_notes?: string
  status?: 'enabled' | 'disabled' | 'pending' | 'suspended'
  reason?: string
}

/**
 * Admin Merchant Service
 * Handles admin-level merchant management operations
 */
export const adminMerchantService = {
  /**
   * Get all merchants (admin only)
   */
  async getAllMerchants(params?: {
    page?: number
    page_size?: number
    search?: string
    status?: string
    is_verified?: boolean
    ordering?: string
  }): Promise<{
    count: number
    next: string | null
    previous: string | null
    results: AdminMerchant[]
  }> {
    return await apiClient.get<{
      count: number
      next: string | null
      previous: string | null
      results: AdminMerchant[]
    }>('/admin/merchants/', { params })
  },

  /**
   * Get merchant by ID (admin only)
   */
  async getMerchantById(id: string): Promise<AdminMerchant> {
    return await apiClient.get<AdminMerchant>(`/admin/merchants/${id}/`)
  },

  /**
   * Create new merchant (admin only)
   */
  async createMerchant(data: AdminMerchantCreateData): Promise<AdminMerchant> {
    return await apiClient.post<AdminMerchant>('/admin/merchants/', data)
  },

  /**
   * Update merchant (admin only)
   */
  async updateMerchant(id: string, data: AdminMerchantUpdateData): Promise<AdminMerchant> {
    return await apiClient.patch<AdminMerchant>(`/admin/merchants/${id}/`, data)
  },

  /**
   * Delete merchant (admin only)
   */
  async deleteMerchant(id: string): Promise<void> {
    await apiClient.delete(`/admin/merchants/${id}/`)
  },

  /**
   * Get merchant statistics (admin only)
   */
  async getMerchantStats(id: string): Promise<AdminMerchantStats> {
    return await apiClient.get<AdminMerchantStats>(`/admin/merchants/${id}/statistics/`)
  },

  /**
   * Verify merchant (admin only)
   */
  async verifyMerchant(id: string, verification_notes?: string): Promise<{
    success: boolean
    message: string
    verified_at: string
  }> {
    return await apiClient.post<{
      success: boolean
      message: string
      verified_at: string
    }>(`/admin/merchants/${id}/verify/`, {
      verification_notes
    })
  },

  /**
   * Remove verification from merchant (admin only)
   */
  async unverifyMerchant(id: string, reason?: string): Promise<{
    success: boolean
    message: string
  }> {
    return await apiClient.post<{
      success: boolean
      message: string
    }>(`/admin/merchants/${id}/unverify/`, {
      reason
    })
  },

  /**
   * Suspend merchant (admin only)
   */
  async suspendMerchant(id: string, reason?: string): Promise<{
    success: boolean
    message: string
  }> {
    return await apiClient.post<{
      success: boolean
      message: string
    }>(`/admin/merchants/${id}/suspend/`, {
      reason
    })
  },

  /**
   * Enable merchant (admin only)
   */
  async enableMerchant(id: string): Promise<{
    success: boolean
    message: string
  }> {
    return await apiClient.post<{
      success: boolean
      message: string
    }>(`/admin/merchants/${id}/enable/`)
  },

  /**
   * Disable merchant (admin only)
   */
  async disableMerchant(id: string, reason?: string): Promise<{
    success: boolean
    message: string
  }> {
    return await apiClient.post<{
      success: boolean
      message: string
    }>(`/admin/merchants/${id}/disable/`, {
      reason
    })
  },

  /**
   * Regenerate API credentials for merchant (admin only)
   */
  async regenerateCredentials(id: string): Promise<{
    success: boolean
    message: string
    api_key: string
    api_secret: string
  }> {
    return await apiClient.post<{
      success: boolean
      message: string
      api_key: string
      api_secret: string
    }>(`/admin/merchants/${id}/regenerate_credentials/`)
  },

  /**
   * Get merchant users (admin only)
   */
  async getMerchantUsers(id: string): Promise<any[]> {
    return await apiClient.get<any[]>(`/admin/merchants/${id}/users/`)
  },

  /**
   * Get merchant sub-merchants (admin only)
   */
  async getMerchantSubMerchants(id: string): Promise<any[]> {
    return await apiClient.get<any[]>(`/admin/merchants/${id}/submerchants/`)
  },

  /**
   * Bulk verify merchants (admin only)
   */
  async bulkVerifyMerchants(data: AdminMerchantBulkAction): Promise<{
    success: boolean
    verified_count: number
    failed_count: number
    details: Array<{
      merchant_id: string
      company_name?: string
      status: 'verified' | 'failed'
      error?: string
    }>
  }> {
    return await apiClient.post<{
      success: boolean
      verified_count: number
      failed_count: number
      details: Array<{
        merchant_id: string
        company_name?: string
        status: 'verified' | 'failed'
        error?: string
      }>
    }>('/admin/merchants-bulk/bulk_verify/', data)
  },

  /**
   * Bulk update merchant status (admin only)
   */
  async bulkUpdateStatus(data: AdminMerchantBulkAction): Promise<{
    success: boolean
    updated_count: number
    failed_count: number
    details: Array<{
      merchant_id: string
      company_name?: string
      old_status?: string
      new_status?: string
      status: 'updated' | 'failed'
      error?: string
    }>
  }> {
    return await apiClient.post<{
      success: boolean
      updated_count: number
      failed_count: number
      details: Array<{
        merchant_id: string
        company_name?: string
        old_status?: string
        new_status?: string
        status: 'updated' | 'failed'
        error?: string
      }>
    }>('/admin/merchants-bulk/bulk_update_status/', data)
  }
}